import type { ReactNode } from 'react'
import type { MenuFooterIconName } from '@/content/printMenu/menuFooterStatements'
import { cn } from '@/lib/utils'

type MenuFooterIconProps = {
  name: MenuFooterIconName
  className?: string
}

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

export function MenuFooterIcon({ name, className }: MenuFooterIconProps) {
  return (
    <svg className={cn('menu-footer-icon size-[15px] shrink-0', className)} {...iconProps}>
      {iconPaths[name]}
    </svg>
  )
}

const iconPaths: Record<MenuFooterIconName, ReactNode> = {
  'shield-check': (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  leaf: (
    <>
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.72 2 8 0 5.5-4.78 10-10 10Z" />
      <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
    </>
  ),
  sparkles: (
    <>
      <path d="m12 3-1.9 5.8H4.4l4.8 3.5-1.9 5.8L12 14.6l4.7 3.5-1.9-5.8 4.8-3.5H13.9L12 3Z" />
      <path d="M5 3v2M19 21v2M3 5h2M21 19h2" />
    </>
  ),
  armchair: (
    <>
      <path d="M19 9V6a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v3" />
      <path d="M3 11v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2a6 6 0 0 0-6-6H9a6 6 0 0 0-6 6Z" />
      <path d="M5 21v-2M19 21v-2" />
    </>
  ),
  hand: (
    <>
      <path d="M18 11V6a2 2 0 0 0-4 0v5" />
      <path d="M14 10V4a2 2 0 0 0-4 0v8" />
      <path d="M10 10V5a2 2 0 0 0-4 0v9a6 6 0 0 0 6 6h2a8 8 0 0 0 8-8v-3a2 2 0 0 0-4 0v2" />
    </>
  ),
  layers: (
    <>
      <path d="m12.83 2.18 8.49 4.92a1 1 0 0 1 0 1.74l-8.49 4.92a2 2 0 0 1-2 0L2.34 8.84a1 1 0 0 1 0-1.74l8.49-4.92a2 2 0 0 1 2 0Z" />
      <path d="m2.34 14.84 8.49 4.92a2 2 0 0 0 2 0l8.49-4.92" />
      <path d="m2.34 19.84 8.49 4.92a2 2 0 0 0 2 0l8.49-4.92" />
    </>
  ),
  droplets: (
    <>
      <path d="M7 16.3c2.2 0 4-1.8 4-4V5.5C9.2 3.6 8.1 2 7 2S4.8 3.6 3 5.5V12.3c0 2.2 1.8 4 4 4Z" />
      <path d="M12 20.7c2.2 0 4-1.8 4-4v-6.8c-1.8-1.9-2.9-3.5-4-3.5s-2.2 1.6-4 3.5v6.8c0 2.2 1.8 4 4 4Z" />
    </>
  ),
  shield: (
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
  ),
  palette: (
    <>
      <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
      <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
      <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
      <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.9 0 1.75-.15 2.52-.42.78-.28 1.3-1.04 1.24-1.86-.06-.82-.64-1.48-1.44-1.62A6 6 0 0 1 12 18c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6c0 .7-.12 1.37-.34 2-.22.63.28 1.24.93 1.08C20.2 20.3 22 16.4 22 12 22 6.5 17.5 2 12 2Z" />
    </>
  ),
  calendar: (
    <>
      <path d="M8 2v4M16 2v4" />
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M3 10h18" />
    </>
  ),
  'map-pin': (
    <>
      <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
  lightbulb: (
    <>
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
      <path d="M9 18h6" />
      <path d="M10 22h4" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </>
  ),
  heart: (
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 12 6c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
  ),
}
